/**
 * Client-side image resizing utility.
 *
 * Resizes images before upload to:
 * - Reduce upload time on slow connections
 * - Prevent oversized images from hitting backend limits
 * - Maintain sufficient quality for OCR (Textract needs ~300 DPI)
 *
 * PDFs are passed through unchanged.
 */

const MAX_DIMENSION = 3000;
const JPEG_QUALITY = 0.90;

/**
 * Resize an image file if it exceeds the max dimension.
 * Returns the original file if no resize is needed or if it's not an image.
 *
 * @param {File} file - The file to potentially resize
 * @param {Object} options - Optional overrides
 * @param {number} options.maxDimension - Max pixels on longest side (default 3000)
 * @param {number} options.quality - JPEG quality 0-1 (default 0.90)
 * @returns {Promise<File>} - The original or resized file
 */
export async function resizeImageIfNeeded(file, options = {}) {
  const { maxDimension = MAX_DIMENSION, quality = JPEG_QUALITY } = options;

  // Only resize images, not PDFs
  if (!file.type.startsWith('image/')) {
    return file;
  }

  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      const { width, height } = img;

      // No resize needed if already within limits
      if (width <= maxDimension && height <= maxDimension) {
        resolve(file);
        return;
      }

      // Calculate new dimensions maintaining aspect ratio
      const ratio = Math.min(maxDimension / width, maxDimension / height);
      const newWidth = Math.round(width * ratio);
      const newHeight = Math.round(height * ratio);

      const canvas = document.createElement('canvas');
      canvas.width = newWidth;
      canvas.height = newHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            // Fallback to original if canvas fails
            resolve(file);
            return;
          }
          // Preserve original filename but ensure .jpg extension for resized images
          const name = file.name.replace(/\.[^.]+$/, '.jpg');
          const resizedFile = new File([blob], name, { type: 'image/jpeg' });
          resolve(resizedFile);
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      // On error, just use the original file
      resolve(file);
    };

    img.src = url;
  });
}
